import { ChevronRight } from "lucide-react";
import { RenderIcon } from "@/components/admin/IconPicker";
import SmartLink from "@/components/ui/SmartLink";

/**
 * GenericSection — fallback renderer for admin/wizard-created sections whose
 * `key` is NOT in Home.jsx's REGISTRY. Without this, any custom section built
 * from the live editor is invisible on the public site.
 *
 * It renders a sensible, on-brand section from a generic `content` object and
 * tags every editable node with data-el / data-field so the live editor can
 * target and restyle it, exactly like the hand-built sections.
 *
 * Prop contract:
 *   content: {
 *     eyebrow?:   string,   // small uppercase kicker above the title
 *     title?:     string,   // main heading (font-serif)
 *     subtitle?:  string,   // supporting paragraph under the title
 *     items?:     Array<{ icon?: string, title?: string, desc?: string, link?: string }>,
 *     cta_text?:  string,   // CTA button label (needs cta_link too)
 *     cta_link?:  string,   // CTA destination (SmartLink: internal or external)
 *     image_url?: string,   // optional supporting image
 *   }
 *
 * Renders null only when there is nothing worth showing (no title, subtitle,
 * items, cta or image), so truly empty sections stay invisible.
 */
export default function GenericSection({ content }) {
  const c = content || {};
  // Păstrăm indexul ORIGINAL din array-ul stocat: `data-field=items.<i>.*` trebuie
  // să pointeze pe poziția reală din DB, nu pe cea din lista filtrată — altfel
  // editarea inline scrie în itemul greșit când există goluri (corupere de date).
  const items = Array.isArray(c.items)
    ? c.items.map((it, origIdx) => ({ it, origIdx })).filter(({ it }) => it)
    : [];
  const hasCta = Boolean(c.cta_text && c.cta_link);
  const hasContent = Boolean(
    c.title || c.subtitle || c.eyebrow || items.length > 0 || hasCta || c.image_url
  );
  if (!hasContent) return null;

  return (
    <section
      data-testid="generic-section"
      className="py-16 lg:py-24"
      style={{ background: "var(--ct-bg)" }}
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        {/* Header */}
        {(c.eyebrow || c.title || c.subtitle) && (
          <div className="max-w-3xl mx-auto text-center mb-10 lg:mb-14">
            {c.eyebrow && (
              <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">
                {c.eyebrow}
              </span>
            )}
            {c.title && (
              <h2
                data-el="title"
                data-field="title"
                className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-3"
              >
                {c.title}
              </h2>
            )}
            {c.subtitle && (
              <p
                data-el="subtitle"
                data-field="subtitle"
                className="text-base sm:text-lg leading-relaxed mt-5"
                style={{ color: "var(--ct-text-muted)" }}
              >
                {c.subtitle}
              </p>
            )}
          </div>
        )}

        {/* Optional supporting image */}
        {c.image_url && (
          <div className="max-w-4xl mx-auto mb-10 lg:mb-14 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--ct-border)" }}>
            <img
              src={c.image_url}
              data-img="image_url"
              alt={c.title || ""}
              className="w-full h-auto object-cover"
            />
          </div>
        )}

        {/* Items grid */}
        {items.length > 0 && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(({ it, origIdx }) => {
              const inner = (
                <>
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
                    style={{ background: "var(--ct-emerald)" }}
                  >
                    <RenderIcon value={it.icon} size={20} color="var(--ct-gold)" />
                  </div>
                  {it.title && (
                    <h3
                      data-el="item_title"
                      data-field={`items.${origIdx}.title`}
                      className="font-serif text-xl mb-2 leading-tight"
                    >
                      {it.title}
                    </h3>
                  )}
                  {it.desc && (
                    <p
                      data-el="item_body"
                      data-field={`items.${origIdx}.desc`}
                      className="text-sm leading-relaxed"
                      style={{ color: "var(--ct-text-muted)" }}
                    >
                      {it.desc}
                    </p>
                  )}
                </>
              );
              return it.link ? (
                <SmartLink
                  key={origIdx}
                  to={it.link}
                  data-testid={`generic-item-${origIdx}`}
                  className="card-premium p-6 h-full block transition-transform hover:scale-[1.02]"
                >
                  {inner}
                </SmartLink>
              ) : (
                <div key={origIdx} data-testid={`generic-item-${origIdx}`} className="card-premium p-6 h-full">
                  {inner}
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        {hasCta && (
          <div className="text-center mt-10 lg:mt-14">
            <SmartLink
              to={c.cta_link}
              data-el="cta"
              data-testid="generic-cta"
              className="btn-gold"
            >
              <span data-field="cta_text">{c.cta_text}</span>
              <ChevronRight size={18} />
            </SmartLink>
          </div>
        )}
      </div>
    </section>
  );
}
